/**
 * Sprint 167 — Los datos personales de los clientes del negocio (Ley Federal de Protección de Datos
 * Personales en Posesión de los Particulares, publicada el 20/03/2025).
 *
 * Quien pide a domicilio por el enlace deja su nombre, su WhatsApp, su dirección y, si quiere, su
 * ubicación. **El negocio es el responsable** de esos datos y nosotros los guardamos por encargo suyo.
 * La ley pide tres cosas que el sistema no hacía:
 *
 *  1. Que el cliente sepa, en el momento en que los escribe, quién los usa y para qué (aviso de
 *     privacidad corto en el enlace + el aviso completo a un toque).
 *  2. Que el negocio pueda borrar los datos de un cliente que lo pida.
 *  3. Que no se guarden para siempre: un plazo, y que se cumpla solo.
 *
 * Aquí viven las reglas que comparten la pantalla, el servidor y el aviso, para que el texto que lee
 * el cliente y lo que hace el sistema no puedan contradecirse.
 */

/**
 * Cuánto se guardan los datos de un cliente después de su ÚLTIMO pedido. Pasado el plazo se borran
 * solos (el pedido se queda: su venta, su total y sus productos, sin nombre, teléfono ni dirección).
 *
 * 12 meses: alcanza para reconocer al cliente que pide cada temporada y para cualquier aclaración de
 * un cobro, y es un plazo que un cliente entiende sin explicación.
 */
export const MESES_CONSERVAR_DATOS_CLIENTE = 12;

/** Lo que queda en el pedido en lugar del nombre, para que el historial siga leyéndose. */
export const NOMBRE_CLIENTE_BORRADO = 'Cliente (datos borrados)';

/** Claves en `configuracion_sistema`. Se leen y escriben sólo por los canales `privacidad:*`. */
export const CLAVE_PRIV_RESPONSABLE = 'privacidad.responsable';
export const CLAVE_PRIV_DOMICILIO = 'privacidad.domicilio';
export const CLAVE_PRIV_CONTACTO = 'privacidad.contacto';

/** Lo que necesita el aviso. Todo esto es público por definición: es la identidad del responsable. */
export interface DatosAvisoPrivacidad {
  /** Como lo conoce el cliente ("Asados Las Espadas"). */
  nombreComercial: string;
  /** Nombre legal de quien responde por los datos (persona o razón social). */
  responsable: string;
  domicilio: string;
  /** Dónde se pide ver, corregir o borrar los datos: un WhatsApp o un correo. */
  contacto: string;
  mesesConservar: number;
  /** false = falta algún dato del dueño; el aviso se muestra igual con lo que hay. */
  completo: boolean;
}

/** Lo que el dueño ve y edita en Configuración → Privacidad de clientes. */
export interface AjustesPrivacidad {
  responsable: string;
  domicilio: string;
  contacto: string;
  /** Lo que se usaría si los campos quedan vacíos (los datos del negocio). */
  sugeridos: { responsable: string; domicilio: string; contacto: string };
  mesesConservar: number;
}

/** Lo que el dueño ve antes de borrar: sólo lo necesario para confirmar que es la persona. */
export interface ClienteParaBorrar {
  encontrado: boolean;
  nombre: string | null;
  pedidos: number;
  /** Folios que impiden borrar hoy (en curso o con dinero pendiente). */
  pendientes: number[];
}

export interface ResultadoBorrado {
  pedidos: number;
  clienteBorrado: boolean;
}

/**
 * Un teléfono escrito dentro de un texto libre (las notas de un pedido, un "llamar al 993…"): una
 * racha de dígitos, espacios o guiones con **10 dígitos o más**. Menos que eso no es un teléfono en
 * México, y así no se tachan fechas ("2026-10-28") ni folios de las notas de auditoría — que son la
 * evidencia de quién autorizó qué (auditoría `cyber` S167, B-2).
 */
export const RE_TELEFONO_EN_TEXTO = /\+?\d[\d\s-]{6,}\d/g;
export const tacharTelefonos = (t: string): string =>
  t.replace(RE_TELEFONO_EN_TEXTO, (m) => (m.replace(/\D/g, '').length >= 10 && !/\d{4}-\d{2}-\d{2}/.test(m) ? '•••' : m));
